import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getPortfolioBudgets } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { formatCLP, formatHours } from '../budget/format'
import FxRatesPanel from './FxRatesPanel'

const PROJECT_W = 260

/** A slim bar for a table cell: spent, then committed, against the budget. */
function MiniBar({ side, kind }) {
  const { budget, used, over } = side
  const spent = kind === 'money' ? side.spent : used
  const committed = kind === 'money' ? side.committed : 0
  const scale = budget && budget > 0 ? budget : Math.max(used, 1)
  const pctOf = (v) => Math.min(100, (v / scale) * 100)
  return (
    <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
      <div className="h-full flex">
        <div style={{ width: `${pctOf(spent)}%`, background: over ? '#dc2626' : '#0f766e' }} />
        {committed > 0 && (
          <div style={{ width: `${pctOf(committed)}%`,
                        background: over ? '#f87171' : '#5eead4' }} />
        )}
      </div>
    </div>
  )
}

function Cell({ side, kind }) {
  const { t } = useTranslation('engineering')
  const fmt = kind === 'money' ? formatCLP : formatHours
  const hasBudget = side.budget != null
  return (
    <td className="px-3 py-2 align-middle" style={{ minWidth: 190 }}>
      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-2 text-xs tabular-nums">
          <span className={side.over ? 'text-red-600 font-semibold' : 'text-gray-700'}>
            {fmt(side.used, { compact: true })}
          </span>
          <span className="text-gray-400">
            {hasBudget ? fmt(side.budget, { compact: true }) : t('budget.noBudget')}
            {side.percent != null && <span className="ml-1.5">{side.percent}%</span>}
          </span>
        </div>
        <MiniBar side={side} kind={kind} />
      </div>
    </td>
  )
}

export default function ProjectBudgets() {
  const { t } = useTranslation('engineering')
  const { user } = useAuth()
  const roles = user?.roles || (user?.role ? [user.role] : [])
  const isAdmin = roles.includes('admin')
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    getPortfolioBudgets()
      .then(d => { if (!cancelled) setData(d) })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.detail || t('budget.loadFailed'))
      })
    return () => { cancelled = true }
  }, [t, reloadKey])

  if (error) return <div className="p-8 text-sm text-red-600">{error}</div>
  if (!data) return <div className="p-8 text-sm text-gray-400">…</div>


  return (
    <div className="p-8 pt-6 space-y-4">
      <div>
        <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">
          {t('budget.portfolioTitle')}
        </h1>
        <p className="text-sm text-gray-500">{t('budget.portfolioSubtitle')}</p>
      </div>

      <FxRatesPanel rates={data.rates} isAdmin={isAdmin}
                    onChanged={() => setReloadKey(k => k + 1)} />

      {data.projects.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-14 text-center text-sm text-gray-400">
          {t('budget.noProjects')}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-auto max-h-[70vh]">
          <table className="text-sm border-separate border-spacing-0 w-full">
            <thead>
              <tr>
                <th style={{ width: PROJECT_W, minWidth: PROJECT_W }}
                    className="sticky left-0 top-0 z-30 bg-white text-left px-4 py-2.5 font-semibold text-gray-600 text-xs border-b border-r border-gray-200">
                  {t('budget.project')}
                </th>
                <th className="sticky top-0 z-20 bg-white text-left px-3 py-2.5 font-semibold text-gray-600 text-xs border-b border-gray-200">
                  {t('budget.hours')}
                </th>
                <th className="sticky top-0 z-20 bg-white text-left px-3 py-2.5 font-semibold text-gray-600 text-xs border-b border-gray-200">
                  {t('budget.money')} (CLP)
                </th>
                <th className="sticky top-0 z-20 bg-white text-right px-4 py-2.5 font-semibold text-gray-600 text-xs border-b border-gray-200 whitespace-nowrap">
                  {t('budget.remaining')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.projects.map((p, i) => {
                const stripe = i % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'
                return (
                  <tr key={p.project_id}>
                    <td style={{ width: PROJECT_W, minWidth: PROJECT_W }}
                        className={`sticky left-0 z-10 ${stripe} px-4 py-2 border-b border-r border-gray-100`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-sm shrink-0"
                              style={{ background: p.color || '#6366f1' }} />
                        <span className="truncate text-gray-800" title={p.name}>{p.name}</span>
                        {p.code && <span className="text-gray-400 text-xs shrink-0">{p.code}</span>}
                      </div>
                    </td>
                    <Cell side={p.hours} kind="hours" />
                    <Cell side={p.money} kind="money" />
                    <td className="px-4 py-2 text-right border-b border-gray-100 tabular-nums text-xs whitespace-nowrap">
                      <div className={p.hours.over ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                        {p.hours.remaining != null ? formatHours(p.hours.remaining, { compact: true }) : '—'}
                      </div>
                      <div className={p.money.over ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                        {p.money.remaining != null ? formatCLP(p.money.remaining, { compact: true }) : '—'}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
